/**
 * Phase 4 — Offline POD upload queue.
 *
 * Drivers operate in patchy signal areas (rural pickups, basement loading
 * bays, motorway dead zones). When `POST /api/driver/jobs/{id}/pod/upload`
 * fails because the network is down, we stash the file in IndexedDB and try
 * again as soon as the browser reports connectivity.
 *
 * Endpoints stay unchanged. The queue is purely a client-side wrapper.
 *
 * Storage:
 *   IndexedDB db `ta247-pod-queue`, store `uploads`. Each record:
 *     { id (autoinc), bookingId, file (Blob), filename, contentType,
 *       authToken, notes?, gps?, attempts, createdAt }
 *
 * Public API:
 *   enqueue({ bookingId, file, authToken, notes?, gps? }) -> Promise<id>
 *   flush() -> Promise<{ flushed, failed, remaining }>
 *   peekCount() -> Promise<number>
 *   subscribe(cb) -> unsubFn   // cb receives current pending count
 */
import axios from "axios";

const DB_NAME = "ta247-pod-queue";
const DB_VERSION = 1;
const STORE = "uploads";

const _subscribers = new Set();

function _openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id", autoIncrement: true });
      }
    };
  });
}

async function _tx(mode, fn) {
  const db = await _openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    Promise.resolve(fn(store)).then((result) => {
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    }).catch(reject);
  });
}

async function _notify() {
  const count = await peekCount();
  _subscribers.forEach((cb) => { try { cb(count); } catch { /* ignore */ } });
}

export async function peekCount() {
  if (typeof indexedDB === "undefined") return 0;
  return _tx("readonly", (store) =>
    new Promise((res, rej) => {
      const r = store.count();
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    })
  ).catch(() => 0);
}

export function subscribe(cb) {
  _subscribers.add(cb);
  peekCount().then(cb).catch(() => {});
  return () => _subscribers.delete(cb);
}

export async function enqueue({ bookingId, file, authToken, notes, gps }) {
  if (!bookingId || !file || !authToken) {
    throw new Error("enqueue: bookingId, file, authToken required");
  }
  if (typeof indexedDB === "undefined") {
    throw new Error("offline_queue_unsupported");
  }
  const record = {
    bookingId,
    file,
    filename: file.name || `pod_${bookingId}.jpg`,
    contentType: file.type || "image/jpeg",
    authToken,
    notes: notes || null,
    gps: gps || null,
    attempts: 0,
    createdAt: new Date().toISOString(),
  };
  const id = await _tx("readwrite", (store) =>
    new Promise((res, rej) => {
      const r = store.add(record);
      r.onsuccess = () => res(r.result);
      r.onerror = () => rej(r.error);
    })
  );
  await _notify();
  // Try to register a background-sync tag — failures are silently ignored
  // because not all browsers (Safari) support it.
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.ready;
      if (reg?.sync?.register) await reg.sync.register("pod-flush");
    }
  } catch { /* ignore */ }
  return id;
}

async function _attemptUpload(record, apiBase) {
  const fd = new FormData();
  fd.append("file", record.file, record.filename);
  const res = await axios.post(`${apiBase}/api/driver/jobs/${record.bookingId}/pod/upload`, fd, {
    headers: { Authorization: `Bearer ${record.authToken}` },
    validateStatus: () => true,
  });
  if (res.status < 200 || res.status >= 300) {
    const detail = typeof res.data === "string" ? res.data : JSON.stringify(res.data || "");
    throw new Error(`upload_failed:${res.status}:${detail.slice(0, 80)}`);
  }
  if (record.notes || record.gps) {
    const params = new URLSearchParams();
    if (record.notes) params.append("notes", record.notes);
    if (record.gps?.lat != null) params.append("gps_lat", String(record.gps.lat));
    if (record.gps?.lng != null) params.append("gps_lng", String(record.gps.lng));
    await axios.post(
      `${apiBase}/api/driver/jobs/${record.bookingId}/pod/details?${params}`,
      null,
      { headers: { Authorization: `Bearer ${record.authToken}` }, validateStatus: () => true }
    ).catch(() => null);
  }
  return res.data || {};
}

async function _list() {
  return _tx("readonly", (store) =>
    new Promise((res, rej) => {
      const r = store.getAll();
      r.onsuccess = () => res(r.result || []);
      r.onerror = () => rej(r.error);
    })
  );
}

async function _delete(id) {
  return _tx("readwrite", (store) =>
    new Promise((res, rej) => {
      const r = store.delete(id);
      r.onsuccess = () => res();
      r.onerror = () => rej(r.error);
    })
  );
}

async function _bump(id, attempts) {
  return _tx("readwrite", (store) =>
    new Promise((res, rej) => {
      const g = store.get(id);
      g.onsuccess = () => {
        const rec = g.result;
        if (!rec) return res();
        rec.attempts = attempts;
        const p = store.put(rec);
        p.onsuccess = () => res();
        p.onerror = () => rej(p.error);
      };
      g.onerror = () => rej(g.error);
    })
  );
}

export async function flush(apiBase = process.env.REACT_APP_BACKEND_URL) {
  if (!apiBase) return { flushed: 0, failed: 0, remaining: 0 };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    const remaining = await peekCount();
    return { flushed: 0, failed: 0, remaining };
  }
  const items = await _list();
  let flushed = 0;
  let failed = 0;
  for (const rec of items) {
    try {
      await _attemptUpload(rec, apiBase);
      await _delete(rec.id);
      flushed += 1;
    } catch (e) {
      failed += 1;
      const nextAttempts = (rec.attempts || 0) + 1;
      await _bump(rec.id, nextAttempts);
      // After 8 attempts we give up to avoid hammering forever, but keep the
      // record so the driver can manually retry from the UI.
      if (nextAttempts > 8) {
        // leave in queue with high attempt count for manual surfacing
      }
    }
  }
  const remaining = await peekCount();
  await _notify();
  return { flushed, failed, remaining };
}

export async function clear() {
  await _tx("readwrite", (store) =>
    new Promise((res, rej) => {
      const r = store.clear();
      r.onsuccess = () => res();
      r.onerror = () => rej(r.error);
    })
  );
  await _notify();
}

// Auto-flush triggers — listen once at module load.
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { flush().catch(() => {}); });
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener?.("message", (e) => {
      if (e.data?.type === "POD_FLUSH_REQUEST") flush().catch(() => {});
    });
  }
}
